import React from 'react';
import { motion } from 'framer-motion';
import { FileCheck, Globe2, Target, ArrowRight, Phone, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const { t, language } = useLanguage();

  const mainServices = [
    {
      id: 'conta-ordem',
      icon: FileCheck,
      titlePt: 'Importação por Conta e Ordem',
      titleEn: 'Import on Behalf of Third Parties',
      whatPt: 'Na importação por conta e ordem, a Costa Valente atua como importadora e executa a operação por conta do adquirente, integrando documentação, logística, desembaraço e conformidade.',
      whatEn: 'In import on behalf, Costa Valente acts as the importer and executes the operation on behalf of the acquirer, integrating documentation, logistics, clearance and compliance.',
      forWhomPt: 'Empresas que desejam importar com apoio técnico completo, sem se preocupar com a operação interna do processo.',
      forWhomEn: 'Companies wishing to import with full technical support, without having to handle the internal operation.',
      stepsPt: [
        'Planejamento tributário e estruturação da operação',
        'Contato com fornecedor internacional e emissão de contrato',
        'Contratação e gestão da logística internacional',
        'Classificação fiscal (NCM) e licenças de importação',
        'Desembaraço aduaneiro e nacionalização',
        'Entrega final ao adquirente',
      ],
      stepsEn: [
        'Tax planning and operation structuring',
        'Supplier contact and contract',
        'International logistics management',
        'Tax classification (NCM) and import licenses',
        'Customs clearance and nationalization',
        'Final delivery to acquirer',
      ],
    },
    {
      id: 'encomenda',
      icon: Globe2,
      titlePt: 'Importação por Encomenda',
      titleEn: 'Import by Order',
      whatPt: 'Na importação por encomenda, a Costa Valente adquire a mercadoria no exterior, realiza a nacionalização e posteriormente a revende ao encomendante no Brasil.',
      whatEn: 'In import by order, Costa Valente acquires the goods abroad, nationalizes them and subsequently resells them to the client in Brazil.',
      forWhomPt: 'Empresas que buscam simplificar o processo, reduzir exposição operacional e receber a mercadoria já nacionalizada.',
      forWhomEn: 'Companies seeking to simplify the process, reduce operational exposure, and receive already-nationalized goods.',
      stepsPt: [
        'Alinhamento da demanda comercial com o cliente',
        'Aquisição direta da mercadoria no exterior',
        'Logística internacional e seguro internacional',
        'Nacionalização e regularização fiscal',
        'Emissão de nota fiscal de revenda',
        'Entrega ao encomendante no Brasil',
      ],
      stepsEn: [
        'Commercial demand alignment with the client',
        'Direct acquisition of goods abroad',
        'International logistics and insurance',
        'Nationalization and fiscal regularization',
        'Issuance of resale invoice',
        'Delivery to the ordering party in Brazil',
      ],
    },
    {
      id: 'assessoria',
      icon: Target,
      titlePt: 'Assessoria Completa em Comércio Exterior',
      titleEn: 'Full International Trade Consulting',
      whatPt: 'Planejamos e coordenamos classificação fiscal, fornecedores, logística, licenças, desembaraço e entrega, com acompanhamento consultivo em todas as etapas.',
      whatEn: 'We plan and coordinate tax classification, suppliers, logistics, licenses, clearance and delivery, with consultative follow-up at every stage.',
      forWhomPt: 'Empresas que operam por conta própria e buscam suporte consultivo estratégico ou empresas em fase de estruturação da área de comércio exterior.',
      forWhomEn: 'Companies operating on their own seeking strategic consulting, or companies structuring their foreign trade area.',
      stepsPt: [
        'Diagnóstico da operação e estruturação do plano',
        'Habilitação RADAR e licenças de importação',
        'Suporte em classificação fiscal (NCM)',
        'Curadoria de fornecedores internacionais',
        'Gestão consultiva de logística e desembaraço',
        'Acompanhamento fiscal e tributário contínuo',
      ],
      stepsEn: [
        'Operation diagnosis and plan structuring',
        'RADAR registration and import licenses',
        'Tax classification (NCM) support',
        'International supplier curation',
        'Consultative logistics and clearance management',
        'Continuous fiscal and tax follow-up',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-24 bg-white">
      <section className="bg-white pt-6 md:pt-14 pb-14 md:pb-20">
        <div className="cv-container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
                {t('NOSSOS SERVIÇOS', 'OUR SERVICES')}
              </span>
            </div>
            <h1 className="text-[40px] sm:text-5xl lg:text-[56px] tracking-tight font-bold text-abyssal leading-[1.05] mb-6">
              {t('Trading internacional com gestão ', 'International trading with ')}
              <span className="text-cobalt">{t('estratégica de ponta a ponta.', 'end-to-end strategic management.')}</span>
            </h1>
            <p className="text-lg text-steel leading-relaxed max-w-2xl">
              {t(
                'Serviços completos em importação com atuação consultiva, operacional e estratégica.',
                'Complete import services with consulting, operational, and strategic support.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-mist border-t border-[#E6E6ED]">
        <div className="cv-container">
          <div className="space-y-8">
            {mainServices.map((s, idx) => (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="bg-white border border-[#E6E6ED] rounded-md p-8 md:p-10"
                data-testid={`service-${s.id}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                  <header className="md:col-span-4">
                    <s.icon className="h-10 w-10 text-cobalt mb-4" strokeWidth={1.5} />
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal mb-2">
                      {t(`MODALIDADE 0${idx + 1}`, `MODEL 0${idx + 1}`)}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-abyssal leading-tight">
                      {t(s.titlePt, s.titleEn)}
                    </h2>
                  </header>
                  <div className="md:col-span-8 space-y-6">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cobalt mb-2">
                        {t('O QUE É', 'WHAT IT IS')}
                      </div>
                      <p className="text-base md:text-lg text-steel leading-relaxed">
                        {t(s.whatPt, s.whatEn)}
                      </p>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cobalt mb-2">
                        {t('PARA QUEM É INDICADA', 'RECOMMENDED FOR')}
                      </div>
                      <p className="text-base text-steel leading-relaxed">
                        {t(s.forWhomPt, s.forWhomEn)}
                      </p>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cobalt mb-3">
                        {t('O QUE A COSTA VALENTE EXECUTA', 'WHAT COSTA VALENTE EXECUTES')}
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        {(language === 'pt' ? s.stepsPt : s.stepsEn).map((step, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm md:text-base text-abyssal">
                            <ChevronRight className="h-4 w-4 text-cobalt flex-shrink-0 mt-1" />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="cv-cta-gradiente relative">
        <div className="absolute inset-0 bg-abyssal/45" aria-hidden="true"></div>
        <div className="relative cv-container py-20 md:py-24 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white">
              {t('FALE COM UM ESPECIALISTA', 'TALK TO A SPECIALIST')}
            </span>
            <span className="h-px w-8 bg-brass"></span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[40px] tracking-tight font-bold text-white mb-8 leading-[1.1] max-w-3xl mx-auto">
            {t('Pronto para elevar o nível da sua importação?', 'Ready to elevate your import operation?')}
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://wa.me/5527996482098" target="_blank" rel="noopener noreferrer" data-testid="services-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-white text-abyssal px-7 py-3.5 rounded-md font-semibold hover:bg-mist transition-colors">
              <Phone className="h-5 w-5" />
              {t('Falar com especialista', 'Talk to specialist')}
            </a>
            <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-cobalt text-white border border-white/30 px-7 py-3.5 rounded-md font-semibold hover:bg-white hover:text-abyssal transition-colors">
              {t('Enviar mensagem', 'Send message')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
